import { useCallback, useMemo } from 'react';
import { useFamilyDocs } from '../../db/useScopedDocs';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { useRxCollection } from 'rxdb/plugins/react';
import type { RxCollection } from 'rxdb';
import {
    formatFindSlipNumber,
    type PhotoDocType,
    type DrawingSheetDocType,
    type OverallDescriptionDocType,
    type FindDocType,
    type SampleDocType
} from '@ast-data/shared';
import { useFamilyProjectIds } from '../projects/useFamilyProjectIds';
import { useFamilyDescriptions } from './useFamilyDescriptions';
import { foldRefsToFindings, type FindingRefs } from './findingRefSets';
import { presetRefsSearch } from './presetRefs';

import { compareNatural } from '../../utils/compare';
import { sampleLabel } from './sampleLabel';

/** The record types that can reference a Planum/Profil description. */
export type RefType =
    'photo' | 'drawingSheet' | 'overallDescription' | 'find' | 'sample';

/**
 * URL segments the field tool mounts its own editors for (see App.tsx). Fotos,
 * Zeichenblätter and Fundzettel are office-only.
 */
const FIELD_ROUTED_SEGMENTS = ['overall-descriptions', 'samples'];

/** Each type's URL segment — where its editor, and its „new“ route, live. */
const ROUTE_SEGMENTS: Record<RefType, string> = {
    photo: 'photos',
    drawingSheet: 'drawing-sheets',
    overallDescription: 'overall-descriptions',
    find: 'finds',
    sample: 'samples'
};

export const TYPE_ORDER: RefType[] = [
    'photo',
    'drawingSheet',
    'overallDescription',
    'find',
    'sample'
];

/** One referencing record, normalized across the types. */
export interface RefRecord {
    /** `${type}:${id}` — unique across types. */
    key: string;
    type: RefType;
    id: string;
    projectId: string;
    primary: string;
    secondary?: string;
    /**
     * Route to that record's editor (scoped to ITS project — may be another
     * phase), or null when this app can't open it. Such a row is listed but not
     * clickable, rather than throwing a field user out into the office.
     */
    to: string | null;
    /** Whether it already references this description. */
    linked: boolean;
    /**
     * Funde and Proben: already references SOME Befund (each may have only
     * one).
     */
    hasBefund?: boolean;
}

/**
 * Everything that references ONE Planum/Profil description, read from the
 * record types that can — photos, Zeichenblätter, Gesamtbeschreibungen, Funde
 * and Proben — and split into what is already linked and what could be.
 *
 * The INVERSE of those records' own Befund pickers: the link is stored on them
 * (`planumIds`/`profileIds`), so it is read by scanning them. Scoped to the
 * Teilmaßnahme FAMILY — a reference may live in another phase's project.
 *
 * The Gesamtbeschreibung is the exception: it covers whole BEFUNDE
 * (`findingIds`), so it is matched on `findingId` and shows on every
 * description of that Befund — linking or unlinking here moves the whole
 * Befund, which is why the caller confirms a removal.
 *
 * The collections come back too: the write side edits the referencing
 * record, not this description.
 */
export function useDescriptionReferences({
    descriptionId,
    findingId,
    kind,
    projectId
}: {
    descriptionId: string;
    /** The Befund this description belongs to. */
    findingId: string;
    /** Which reference array to read on the referencing records. */
    kind: 'planum' | 'profile';
    projectId: string;
}) {
    const intl = useIntl();
    const familyIds = useFamilyProjectIds(projectId);
    const { results: photos } = useFamilyDocs<PhotoDocType>(
        'photos',
        familyIds
    );
    const { results: drawingSheets } = useFamilyDocs<DrawingSheetDocType>(
        'drawing_sheets',
        familyIds
    );
    const { results: overallDescriptions } =
        useFamilyDocs<OverallDescriptionDocType>(
            'overall_descriptions',
            familyIds
        );
    const { results: finds } = useFamilyDocs<FindDocType>('finds', familyIds);
    const { results: samples } = useFamilyDocs<SampleDocType>(
        'samples',
        familyIds
    );

    // Collections for the write side. `planumIds`/`profileIds` are string arrays
    // on all of them, so we treat the target loosely (one code path).
    const photosCol = useRxCollection<PhotoDocType>('photos');
    const drawingSheetsCol =
        useRxCollection<DrawingSheetDocType>('drawing_sheets');
    const overallDescriptionsCol = useRxCollection<OverallDescriptionDocType>(
        'overall_descriptions'
    );
    const findsCol = useRxCollection<FindDocType>('finds');
    const samplesCol = useRxCollection<SampleDocType>('samples');
    const collectionByType = useMemo<Record<RefType, RxCollection | null>>(
        () => ({
            photo: photosCol as unknown as RxCollection | null,
            drawingSheet: drawingSheetsCol as unknown as RxCollection | null,
            overallDescription:
                overallDescriptionsCol as unknown as RxCollection | null,
            find: findsCol as unknown as RxCollection | null,
            sample: samplesCol as unknown as RxCollection | null
        }),
        [
            photosCol,
            drawingSheetsCol,
            overallDescriptionsCol,
            findsCol,
            samplesCol
        ]
    );

    // The family's descriptions, only to fold a Gesamtbeschreibung's refs up to
    // their Befund — older ones still name single Planum/Profil rows.
    const { planums: familyPlanums, profiles: familyProfiles } =
        useFamilyDescriptions(projectId);

    // Befund id -> the Gesamtbeschreibungen covering it, folded so old and new
    // records read alike.
    const coveredFindingIds = useMemo(() => {
        const map = new Map<string, Set<string>>();
        for (const od of overallDescriptions) {
            map.set(
                od.id,
                new Set(
                    foldRefsToFindings(od, familyPlanums, familyProfiles)
                        .findingIds
                )
            );
        }
        return map;
    }, [overallDescriptions, familyPlanums, familyProfiles]);

    // The ref set to store on a Gesamtbeschreibung when this description's
    // BEFUND is linked or unlinked. Its existing refs are folded first, so
    // touching an older record normalises it in the same write.
    const overallDescriptionRefs = useCallback(
        (data: Record<string, unknown>, shouldLink: boolean): FindingRefs => {
            const folded = foldRefsToFindings(
                {
                    planumIds: (data.planumIds as string[] | undefined) ?? [],
                    profileIds: (data.profileIds as string[] | undefined) ?? [],
                    findingIds: (data.findingIds as string[] | undefined) ?? []
                },
                familyPlanums,
                familyProfiles
            );
            const next = new Set(folded.findingIds);
            if (shouldLink) {
                next.add(findingId);
            } else {
                next.delete(findingId);
            }
            return { planumIds: [], profileIds: [], findingIds: [...next] };
        },
        [familyPlanums, familyProfiles, findingId]
    );

    const idField = kind === 'planum' ? 'planumIds' : 'profileIds';
    const inField = useLocation().pathname.startsWith('/field');

    // `rest` is `<id>/edit` or `new?…`, under the record's own project.
    const route = useCallback(
        (recordProjectId: string, segment: string, rest: string) => {
            if (!inField) {
                return `/office/projects/${recordProjectId}/${segment}/${rest}`;
            }
            return FIELD_ROUTED_SEGMENTS.includes(segment)
                ? `/field/${recordProjectId}/${segment}/${rest}`
                : null;
        },
        [inField]
    );

    /**
     * Per type, the route creating a record that already references this
     * description — in THIS project — or null where this app has no editor for
     * it. A Gesamtbeschreibung is preset with the Befund, see above.
     */
    const newRecordTo = useMemo(() => {
        const map = {} as Record<RefType, string | null>;
        for (const type of TYPE_ORDER) {
            const search =
                type === 'overallDescription'
                    ? presetRefsSearch('findingIds', findingId)
                    : presetRefsSearch(idField, descriptionId);
            map[type] = route(projectId, ROUTE_SEGMENTS[type], `new${search}`);
        }
        return map;
    }, [route, projectId, idField, descriptionId, findingId]);

    const records = useMemo<RefRecord[]>(() => {
        const dash = '—';
        const editRoute = (
            type: RefType,
            recordProjectId: string,
            id: string
        ) => route(recordProjectId, ROUTE_SEGMENTS[type], `${id}/edit`);
        const isLinked = (row: { [key: string]: unknown }) =>
            ((row[idField] as string[] | undefined) ?? []).includes(
                descriptionId
            );
        const out: RefRecord[] = [];
        for (const photo of photos) {
            out.push({
                key: `photo:${photo.id}`,
                type: 'photo',
                id: photo.id,
                projectId: photo.projectId,
                primary: photo.fileName || dash,
                secondary: photo.motif || undefined,
                to: editRoute('photo', photo.projectId, photo.id),
                linked: isLinked(photo)
            });
        }
        for (const sheet of drawingSheets) {
            out.push({
                key: `drawingSheet:${sheet.id}`,
                type: 'drawingSheet',
                id: sheet.id,
                projectId: sheet.projectId,
                primary: intl.formatMessage(
                    { id: 'descriptionRefs.item.drawingSheet' },
                    { number: sheet.drawingSheetNumber || dash }
                ),
                secondary: sheet.fileName || undefined,
                to: editRoute('drawingSheet', sheet.projectId, sheet.id),
                linked: isLinked(sheet)
            });
        }
        for (const od of overallDescriptions) {
            out.push({
                key: `overallDescription:${od.id}`,
                type: 'overallDescription',
                id: od.id,
                projectId: od.projectId,
                primary: intl.formatMessage(
                    { id: 'descriptionRefs.item.overallDescription' },
                    { number: od.overallDescriptionNumber || dash }
                ),
                secondary: od.designation || undefined,
                to: editRoute('overallDescription', od.projectId, od.id),
                // Read from the BEFUND, not this description.
                linked: coveredFindingIds.get(od.id)?.has(findingId) ?? false
            });
        }
        for (const find of finds) {
            out.push({
                key: `find:${find.id}`,
                type: 'find',
                id: find.id,
                projectId: find.projectId,
                primary: intl.formatMessage(
                    { id: 'descriptionRefs.item.find' },
                    { number: formatFindSlipNumber(find) || dash }
                ),
                secondary: find.label || undefined,
                to: editRoute('find', find.projectId, find.id),
                linked: isLinked(find),
                // A Fund may reference only ONE Befund — flag an existing link so
                // adding this description asks to replace it.
                hasBefund:
                    find.planumIds.length +
                        find.profileIds.length +
                        find.findingIds.length >
                    0
            });
        }
        for (const sample of samples) {
            out.push({
                key: `sample:${sample.id}`,
                type: 'sample',
                id: sample.id,
                projectId: sample.projectId,
                primary: sampleLabel(intl, sample),
                secondary: sample.samplingReason || undefined,
                to: editRoute('sample', sample.projectId, sample.id),
                linked: isLinked(sample),
                hasBefund:
                    sample.planumIds.length + sample.profileIds.length > 0
            });
        }
        return out;
    }, [
        idField,
        descriptionId,
        findingId,
        coveredFindingIds,
        photos,
        drawingSheets,
        overallDescriptions,
        finds,
        samples,
        route,
        intl
    ]);

    // Split into linked (listed) and available (add options) per type.
    const byType = useMemo(() => {
        const map = new Map<
            RefType,
            { linked: RefRecord[]; available: RefRecord[] }
        >();
        for (const type of TYPE_ORDER) {
            map.set(type, { linked: [], available: [] });
        }
        for (const record of records) {
            const bucket = map.get(record.type);
            if (!bucket) {
                continue;
            }
            (record.linked ? bucket.linked : bucket.available).push(record);
        }
        for (const bucket of map.values()) {
            bucket.available.sort((firstRecord, secondRecord) =>
                compareNatural(firstRecord.primary, secondRecord.primary)
            );
        }
        return map;
    }, [records]);

    const linkedTotal = useMemo(
        () => records.filter((record) => record.linked).length,
        [records]
    );

    return {
        records,
        byType,
        linkedTotal,
        collectionByType,
        idField,
        overallDescriptionRefs,
        newRecordTo
    };
}
